import React from "react";
import { Checkbox, Input, Radio, RadioGroup } from "@pd/pd-ui-kit";
import { Control, Controller, UseFormMethods } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { IEmployeeProfileForm } from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/types";
import { useCardEditAccess } from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/useCardEditAccess";
import { FileFieldController } from "~/shared/ui/Fields/FileField";

import FormDateField from "./FormDateField";
import FormField from "./FormField";

import s from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/EmployeeProfileForm.less";

interface IPersonalDataSectionProps {
  control: Control<IEmployeeProfileForm>;
  errors: UseFormMethods<IEmployeeProfileForm>["errors"];
  setValue: UseFormMethods<IEmployeeProfileForm>["setValue"];
  photo?: IEmployeeProfileForm["photo"];
}

const PersonalDataSection = ({
  control,
  errors,
  setValue,
  photo,
}: IPersonalDataSectionProps) => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isAdd = id === "add";
  const { canEditAll, canEditAny } = useCardEditAccess();

  return (
    <section className={s.section}>
      <h2 className={s.sectionTitle}>{t("employee.listTitleData")}</h2>
      <div className={s.personalLayout}>
        <div className={s.personalFields}>
          {!isAdd && (
            <FormField label={t("employee.id")}>
              <Controller
                name="id"
                control={control}
                render={({ value }) => (
                  <Input value={String(value ?? "")} disabled />
                )}
              />
            </FormField>
          )}

          <div className={s.grid3}>
            <FormField label={t("employee.surname")} required>
              <Controller
                name="surname"
                control={control}
                render={({ onChange, value, onBlur }) => (
                  <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={!canEditAll}
                    status={errors.surname ? "error" : undefined}
                    errorText={errors.surname?.message}
                  />
                )}
              />
            </FormField>
            <FormField label={t("employee.name")} required>
              <Controller
                name="name"
                control={control}
                render={({ onChange, value, onBlur }) => (
                  <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={!canEditAll}
                    status={errors.name ? "error" : undefined}
                    errorText={errors.name?.message}
                  />
                )}
              />
            </FormField>
            <FormField label={t("employee.middleName")}>
              <Controller
                name="middleName"
                control={control}
                render={({ onChange, value, onBlur }) => (
                  <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={!canEditAll}
                  />
                )}
              />
              <Controller
                name="noMiddleName"
                control={control}
                render={({ onChange, value }) => (
                  <Checkbox
                    checked={value}
                    disabled={!canEditAll}
                    onChange={(e) => {
                      onChange(e.target.checked);
                      if (e.target.checked) {
                        setValue("middleName", "");
                      }
                    }}
                  >
                    {t("employee.noMiddleName")}
                  </Checkbox>
                )}
              />
            </FormField>
          </div>

          <FormField
            label={t("employee.gender")}
            error={errors.gender?.message}
            required
          >
            <Controller
              name="gender"
              control={control}
              render={({ onChange, value }) => (
                <RadioGroup
                  className={s.genderRadioGroup}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  disabled={!canEditAll}
                >
                  <Radio className={s.genderOption} value="Male">
                    {t("employee.male")}
                  </Radio>
                  <Radio className={s.genderOption} value="Female">
                    {t("employee.female")}
                  </Radio>
                </RadioGroup>
              )}
            />
          </FormField>

          <div className={s.grid2}>
            <FormDateField
              name="birthDay"
              control={control}
              label={t("employee.birthDay")}
              disabled={!canEditAll}
              error={errors.birthDay?.message}
              required
            />
            <FormField label={t("employee.workPhone")} required>
              <Controller
                name="workPhone"
                control={control}
                render={({ onChange, value, onBlur }) => (
                  <Input
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                    onBlur={onBlur}
                    disabled={!canEditAll}
                    status={errors.workPhone ? "error" : undefined}
                    errorText={errors.workPhone?.message}
                  />
                )}
              />
            </FormField>
          </div>
        </div>

        <div className={s.photoBlock}>
          <FileFieldController
            name="photoId"
            control={control}
            variant="photo"
            label={t("employee.photoID")}
            accept=".png,.jpg,.jpeg"
            maxSizeMb={5}
            disabled={!canEditAny}
            error={errors.photoId?.message}
            initialFile={
              photo
                ? {
                    id: photo.id,
                    name: photo.name || t("employee.photoID"),
                    link: photo.link || "",
                  }
                : null
            }
          />
        </div>
      </div>
    </section>
  );
};

export default PersonalDataSection;
